import { Box, Typography, Grid, CircularProgress, IconButton, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import HomeIcon from '@mui/icons-material/Home';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityLogActions } from '../../Services/_actions/ActivityLogs/ActivityLogActions.ts';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import { useNavigate } from 'react-router';
import { FilterObj, PaginatedResponse } from '../../interfaces/Management.interface.ts';
import { dateFormate } from '../../utils/helper.jsx';
import Pagination from '../FlowBuilder/Common/Pagination/index.tsx';
import CommonTable from '../Shared/Tables/TableV1.tsx';

const ActivityLogs: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [tableFilters, setTableFilters] = useState<FilterObj>({ page: 0, search: '', limit: 20 });
  const actions = ActivityLogActions();
  const [activityResponse, setActivityResponse] = useState<PaginatedResponse>({
    count: 0,
    list: [],
  });
  const navigate = useNavigate();

  const getActivityLogs = async (tableFilters: FilterObj) => {
    setIsLoading(true);
    setActivityResponse({ count: 0, list: [] });
    try {
      const res: any = await actions.getActivityLogs(tableFilters);
      if (res?.status === 200) {
        setActivityResponse({ list: res?.data?.list || [], count: res?.data?.total_count || 0 });
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    getActivityLogs(tableFilters);
  }, []);
  const handleChangePage = (event, newPage) => {
    getActivityLogs({ ...tableFilters, page: newPage });
    setTableFilters({ ...tableFilters, page: newPage });
  };
  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value) || 20;
    getActivityLogs({
      ...tableFilters,
      limit: value,
      page: 0,
    });
    setTableFilters({
      ...tableFilters,
      limit: value,
      page: 0,
    });
  };

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      href: '/',
    },
    { label: 'Activity Log Details' },
  ];

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'username',
        header: 'User Name',
        enableSorting: false,
        size: 80,
      },
      {
        accessorKey: 'method',
        header: 'Method',
        enableSorting: false,
        size: 50,
      },
      {
        accessorKey: 'path',
        header: 'API Path',
        enableSorting: false,
        size: 150,
      },
      {
        accessorKey: 'status_code',
        header: 'Status Code',
        enableSorting: false,
        size: 50,
        Cell: ({ cell }: any) => (
          <Typography sx={{ color: cell.getValue() === 200 ? 'green' : 'red', fontWeight: 'bold' }}>
            {cell?.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: 'ip_address',
        header: 'IP Address',
        enableSorting: false,
        size: 100,
      },
      {
        accessorKey: 'timestamp',
        header: 'Date',
        enableSorting: false,
        size: 50,
        Cell: ({ cell }: any) => <>{dateFormate(cell?.getValue(), 'DD-MM-YYYY')}</>,
      },
      {
        accessorKey: 'timestamp',
        header: 'Time',
        enableSorting: false,
        size: 50,
        Cell: ({ cell }: any) => <>{dateFormate(cell?.getValue(), 'hh:mm:a')}</>,
      },
    ],
    []
  );

  return (
    <Box
      sx={{
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }}
    >
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item: { label: string; href: string }) => {
          if (item) {
            navigate(item?.href);
          }
        }}
      />
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Activity Log Details
          </Typography>
        </Grid>
      </Grid>
      <CommonTable
        columns={columns}
        data={activityResponse?.list || []}
        muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 270px)', width: '100%' } }}
        state={{
          showColumnFilters: false,
          isLoading,
        }}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableHiding={false}
        enableStickyHeader={true}
        enableTopToolbar={false}
        enableBottomToolbar={true}
        initialState={{
          density: 'comfortable',
        }}
        enablePagination={false}
        manualPagination={false}
        renderBottomToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }}>
            <Pagination
              totalCount={activityResponse?.count || 0}
              rowsPerPage={tableFilters?.limit}
              page={tableFilters?.page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        )}
      />
    </Box>
  );
};

export default ActivityLogs;
